/**
 * @engine/vfx — Call emitter: wires node functions into a stage main().
 *
 * For each node instance the emitter resolves every input to an expression —
 * a connected producer's SSA variable, a baked literal, or a texture binding —
 * and emits one call line. Operator (data-flow) dependencies are emitted first
 * in topological order; block (flow) nodes follow their context order.
 */

import type { VFXGraph } from '../../VFXGraph';
import type { VFXNodeInstance, VFXPortDefinition, VFXGraphVariable, VFXDataType } from '../../types';
import { vfxNodeRegistry } from '../../VFXNodeRegistry';
import { valueToSlang } from './slangTypes';
import { sanitizeIdent } from './scan';
import { NodeFunctionCache, portSlangType, type EmittedNodeFunction } from './NodeFunctionEmitter';
import { TweakAllocator, isTweakableType, tweakAccessExpr } from './Tweaks';
import { resolveVariableSlot } from './variableSlots';

// ============================================================================
// TYPES
// ============================================================================

export interface CallEmissionResult {
  /** Call/let lines to place inside main(), in execution order. */
  readonly lines: string[];
  /**
   * Node instance owning each line of {@link lines} (parallel array; null for
   * structural lines). Feeds the stage source map → editor error highlighting.
   */
  readonly lineOwners: readonly (string | null)[];
  /** Node functions used (module-level definitions, first-use order). */
  readonly functions: readonly EmittedNodeFunction[];
  /** Texture variable slots referenced (for header binding declarations). */
  readonly textureSlots: ReadonlySet<number>;
  /** Env builtin names required by any called function. */
  readonly builtinsUsed: ReadonlySet<string>;
  /** Non-fatal codegen warnings. */
  readonly warnings: string[];
}

// ============================================================================
// NAMED VARIABLE RESOLUTION
// ============================================================================

/**
 * Resolve a named graph variable to its packed vfxVariables access expression.
 * Slot convention comes from `resolveVariableSlot` — the SAME resolver the
 * runtime writers use, so shader reads and buffer writes always agree.
 */
export function namedVariableAccess(
  variables: readonly VFXGraphVariable[],
  ref: string
): { expr: string; type: VFXDataType } | null {
  const resolved = resolveVariableSlot(variables, ref);
  if (!resolved) return null;
  const { category, slot } = resolved;

  switch (category) {
    case 'float':
      return { expr: `vfxVariables.floats[${slot >> 2}][${slot & 3}]`, type: 'float' };
    case 'vec2': {
      const half = (slot & 1) === 0 ? 'xy' : 'zw';
      return { expr: `vfxVariables.vec2s[${slot >> 1}].${half}`, type: 'vec2' };
    }
    case 'vec3':
      return { expr: `vfxVariables.vec3s[${slot}].xyz`, type: 'vec3' };
    case 'color':
      return { expr: `vfxVariables.colors[${slot}]`, type: 'color' };
    default:
      return null; // textures are binding references, not uniform reads
  }
}

// ============================================================================
// CALL EMITTER
// ============================================================================

const TEXTURE_GETTER_TYPES = new Set(['vfx.variable.get_texture', 'vfx.variable.get_pointmap']);

export class CallEmitter {
  private readonly lines: string[] = [];
  private readonly lineOwners: (string | null)[] = [];
  private readonly warnings: string[] = [];
  private readonly textureSlots = new Set<number>();
  private readonly builtinsUsed = new Set<string>();
  /** (nodeId → portId → expression) for already-emitted producers. */
  private readonly producedValues = new Map<string, Map<string, string>>();
  private readonly emittedNodes = new Set<string>();

  constructor(
    private readonly graph: VFXGraph,
    private readonly fnCache: NodeFunctionCache,
    /** Env builtin names the surrounding stage main() provides. */
    private readonly availableBuiltins: ReadonlySet<string>,
    /**
     * Editor live-preview: route unconnected float-family inputs through
     * uniform tweak slots instead of baking literals. Shared per system across
     * both stages so the slot layout matches.
     */
    private readonly tweaks: TweakAllocator | null = null
  ) {}

  result(): CallEmissionResult {
    return {
      lines: this.lines,
      lineOwners: this.lineOwners,
      functions: this.fnCache.all(),
      textureSlots: this.textureSlots,
      builtinsUsed: this.builtinsUsed,
      warnings: this.warnings,
    };
  }

  private pushLine(text: string, owner: string | null): void {
    this.lines.push(text);
    this.lineOwners.push(owner);
  }

  /**
   * Emit operator (pure data-flow) dependencies of the given nodes, in
   * topological order, then the nodes themselves in list order.
   */
  emitNodes(nodes: readonly VFXNodeInstance[]): void {
    for (const operator of this.collectOperatorDependencies(nodes)) {
      this.emitNode(operator);
    }
    for (const node of nodes) {
      this.emitNode(node);
    }
  }

  /** Emit a single node call (idempotent per node instance). */
  emitNode(node: VFXNodeInstance): void {
    if (this.emittedNodes.has(node.id)) return;
    this.emittedNodes.add(node.id);

    const def = vfxNodeRegistry.get(node.type);
    if (!def) {
      this.warnings.push(`Unknown node type: ${node.type} (${node.id})`);
      return;
    }

    // Texture getters produce a binding reference, not code.
    if (TEXTURE_GETTER_TYPES.has(node.type)) {
      const slot = Number(node.inputValues.variableId ?? 0) || 0;
      this.textureSlots.add(slot);
      this.setProduced(node.id, def.outputs[0]?.id ?? 'texture', `vfxTexture${slot}`);
      return;
    }

    // Named variable getter — resolved to a packed vfxVariables access.
    if (def.requiresVariableResolution) {
      const name = String(node.inputValues.variableName ?? '');
      const resolved = namedVariableAccess(this.graph.getVariables(), name);
      const varName = `v_${sanitizeIdent(node.id)}_value`;
      if (!resolved) {
        this.warnings.push(`Get Variable: unknown variable "${name}" (${node.id}) — using 0.0`);
        this.pushLine(`let ${varName} = 0.0;`, node.id);
      } else {
        this.pushLine(`let ${varName} = ${resolved.expr};`, node.id);
      }
      this.setProduced(node.id, 'value', varName);
      return;
    }

    const fn = this.fnCache.getOrEmit(def, node);
    if (!fn) return; // structural node (no shader body)

    for (const b of fn.builtins) {
      this.builtinsUsed.add(b.name);
      if (!this.availableBuiltins.has(b.name)) {
        this.warnings.push(
          `Node "${def.name}" (${node.id}) uses '${b.name}' which is not available in this stage`
        );
      }
    }

    // Resolve arguments
    const args: string[] = fn.builtins.map(b => b.name);
    for (const port of fn.dataParams) {
      args.push(this.resolveInput(node, port));
    }

    const v = (portId: string) => `v_${sanitizeIdent(node.id)}_${portId}`;

    switch (fn.returnKind) {
      case 'void':
        this.pushLine(`${fn.name}(${args.join(', ')});`, node.id);
        break;
      case 'single': {
        const out = fn.outputs[0];
        this.pushLine(`let ${v(out.id)} = ${fn.name}(${args.join(', ')});`, node.id);
        this.setProduced(node.id, out.id, v(out.id));
        break;
      }
      case 'outparams': {
        for (const out of fn.outputs) {
          this.pushLine(`${portSlangType(out)} ${v(out.id)};`, node.id);
          this.setProduced(node.id, out.id, v(out.id));
        }
        this.pushLine(`${fn.name}(${[...args, ...fn.outputs.map(o => v(o.id))].join(', ')});`, node.id);
        break;
      }
    }
  }

  // --------------------------------------------------------------------------
  // Input resolution
  // --------------------------------------------------------------------------

  private resolveInput(node: VFXNodeInstance, port: VFXPortDefinition): string {
    const edge = this.graph
      .getEdges()
      .find(e => e.to.nodeId === node.id && e.to.portId === port.id);

    // Texture ports: binding reference (from a texture getter) or fallback slot 0
    if (port.type === 'texture2d' || port.type === 'pointmap') {
      if (edge) {
        const source = this.graph.getNode(edge.from.nodeId);
        if (source && TEXTURE_GETTER_TYPES.has(source.type)) {
          const slot = Number(source.inputValues.variableId ?? 0) || 0;
          this.textureSlots.add(slot);
          return `vfxTexture${slot}`;
        }
        this.warnings.push(
          `Texture input ${node.type}.${port.id} connected to non-texture-getter ${edge.from.nodeId} — using slot 0`
        );
      }
      this.textureSlots.add(0);
      return 'vfxTexture0';
    }

    if (edge) {
      const produced = this.producedValues.get(edge.from.nodeId)?.get(edge.from.portId);
      if (produced) return produced;
      // Producer not emitted (not an operator / not reachable) — emit it now if
      // it's a pure data node; otherwise fall through to the literal.
      const source = this.graph.getNode(edge.from.nodeId);
      if (source) {
        this.emitNode(source);
        const late = this.producedValues.get(edge.from.nodeId)?.get(edge.from.portId);
        if (late) return late;
      }
      this.warnings.push(
        `Input ${node.type}.${port.id} is connected to ${edge.from.nodeId}:${edge.from.portId} which produced no value — using default`
      );
    }

    const value = node.inputValues[port.id] ?? port.defaultValue;
    const options = (port as { options?: readonly string[] }).options;

    // Tweak mode: float-family literals become uniform slot reads so the
    // editor can update them without recompiling. Enum-like ints keep baking
    // (they steer loops/branches — structural).
    if (this.tweaks && isTweakableType(port.type as VFXDataType)) {
      const binding = this.tweaks.allocate(node.id, port.id, port.type as VFXDataType, value);
      if (binding) return tweakAccessExpr(binding.slot, binding.type);
      this.warnings.push(
        `Tweak slot capacity exhausted — ${node.type}.${port.id} baked as a literal (edits recompile)`
      );
    }

    return valueToSlang(value, port.type as VFXDataType, options);
  }

  private setProduced(nodeId: string, portId: string, expr: string): void {
    let map = this.producedValues.get(nodeId);
    if (!map) {
      map = new Map();
      this.producedValues.set(nodeId, map);
    }
    map.set(portId, expr);
  }

  // --------------------------------------------------------------------------
  // Operator dependency collection (topological, dependencies first)
  // --------------------------------------------------------------------------

  private collectOperatorDependencies(contextNodes: readonly VFXNodeInstance[]): VFXNodeInstance[] {
    const edges = this.graph.getEdges();
    const nodeById = new Map(this.graph.getNodes().map(n => [n.id, n]));

    const isOperatorNode = (node: VFXNodeInstance): boolean => {
      const def = vfxNodeRegistry.get(node.type);
      if (!def) return false;
      return (
        def.category === 'operator' ||
        def.category === 'variable' ||
        def.category === 'sampling' ||
        (def.contexts && def.contexts.length === 0)
      );
    };

    // BFS upstream from the context nodes
    const operatorIds = new Set<string>();
    const queue = contextNodes.map(n => n.id);
    const visited = new Set<string>();
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      for (const edge of edges) {
        if (edge.to.nodeId !== id) continue;
        const source = nodeById.get(edge.from.nodeId);
        if (source && isOperatorNode(source) && !visited.has(source.id)) {
          operatorIds.add(source.id);
          queue.push(source.id);
        }
      }
    }

    // DFS post-order → dependencies first
    const sorted: VFXNodeInstance[] = [];
    const perm = new Set<string>();
    const temp = new Set<string>();
    const visit = (id: string) => {
      if (perm.has(id) || temp.has(id)) return; // cycle → skip (validator reports)
      temp.add(id);
      for (const edge of edges) {
        if (edge.to.nodeId === id && operatorIds.has(edge.from.nodeId)) {
          visit(edge.from.nodeId);
        }
      }
      temp.delete(id);
      perm.add(id);
      const node = nodeById.get(id);
      if (node) sorted.push(node);
    };
    for (const id of operatorIds) visit(id);
    return sorted;
  }
}
